import React from "react";
import { Button, Form, Modal } from "react-bootstrap";

import LoadMask from "../../../../components/LoadMask";
import Item from "../../../../models/Item";

import "./ProductShareModal.scss";
import "./ProductShareViaEmailModal.scss";

/**
 *
 * @param {String[]} initialEmails
 * @param {String} initialMessage
 * @param {String} initialSubject
 * @param {Function} onClose
 * @param {Function} onSave
 * @param {Item[]} items
 */
export default function ProductShareViaEmailModal({
	emails: initialEmails,
	message: initialMessage,
	subject: initialSubject,
	items,
	onClose,
	onSave,
}) {
	const [errors, setErrors] = React.useState([]);
	const [emails, setEmails] = React.useState(initialEmails?.reduce((acc, email, idx) => {
		acc[idx] = email;
		return acc;
	}, {}) ?? { 0: "" });
	const [message, setMessage] = React.useState(initialMessage);
	const [subject, setSubject] = React.useState();
	const [processing, setProcessing] = React.useState(false);
	const touched = React.useRef();
	const emailsContainerRef = React.useRef();

	// MOUNT
	React.useEffect(() => {
		focus_email();
	}, []);

	const focus_email = () => {
		// account for render time
		setTimeout(() => {
			const emailInput = Array.from(emailsContainerRef.current.querySelectorAll(`input[type = "email"]`)).at(-1);
			emailInput?.focus();
		}, 50);
	}

	const inner_on_close = () => {
		if (!emails) {
			do_close();
		}
	}

	const do_close = () => {
		onClose?.();
	}

	const save = async () => {
		setProcessing(true);
		setErrors([]);

		try {
			const result = await Item.Call({
				name: "Share_Via_Emails",
				payload: {
					emails,
					subject,
					message,
					item_handles: items?.map(it => it.handle),
				}
			});

			if (!result.success) {
				throw new Error(result.result);
			}

			onSave?.(result.result);
			do_close();
		}
		catch (e) {
			console.warn("Failed to send text", e);
			setProcessing(false);
			setErrors(["There was a problem sending the text message."]);
		}
	}

	const onKeyUp = e => {
		if (e.key.toLowerCase() === "escape") {
			key_escape(e);
		}
		else if (e.key.toLowerCase() === "enter" && !(e.target instanceof HTMLTextAreaElement)) {
			save();
		}
		touched.current = true;
	}

	const key_escape = e => {
		if (!touched.current) {
			onClose?.();
		}
		e.preventDefault();
	}

	const email_change = (idx, e) => {
		setEmails(current => {
			current[idx] = e.target.value;
			return { ...current };
		});
	}

	const add_email = () => {
		setEmails(current => ({ ...current, [Date.now()]: "" }));
		focus_email();
	}

	const remove_email = idx => {
		setEmails(current => {
			delete current[idx];
			return { ...current };
		});
	}


	return (
		<Modal
			className={"_boss dialog email-input-dialog"}
			show={true}
			onHide={inner_on_close}
			onEscapeKeyDown={key_escape}
			centered
			backdrop
		>
			<Modal.Header>
				<Modal.Title>Share Via Email | {items.length} Item{items.length !== 1 ? "s" : ""}</Modal.Title>
				<button className="btn-close" area-label="Close" onClick={do_close}></button>
			</Modal.Header>
			<Modal.Body>
				<LoadMask show={processing} />
				<section data-component="ProductShareViaEmailModal">
					<section id="info-form">
						<section ref={emailsContainerRef} id="emails">
							{emails && Object.entries(emails)?.map(([idx, email]) => (
								<div key={idx} data-only={Object.keys(emails).length === 1} data-last={idx === Object.keys(emails).at(-1)}>
									{email !== null && (
										<>
											<Form.FloatingLabel label={"Email" + (idx === "0" ? " *" : "")}>
												<Form.Control
													required={idx === "0"}
													autoComplete="off"
													value={emails[idx] ?? ""}
													type="email"
													placeholder=""
													onChange={e => email_change(idx, e)}
													onKeyDownCapture={onKeyUp}
												/>
											</Form.FloatingLabel>
											<Button
												data-action="add-email"
												data-allowed={Object.entries(emails).filter(([idx, email]) => !email).length === 0}
												onClick={add_email}>+</Button>
											<Button data-action="remove-email" onClick={() => remove_email(idx)}>-</Button>
										</>
									)}
								</div>
							))}
						</section>

						<Form.FloatingLabel label={initialSubject ? `Subject: "${initialSubject}"` : "Subject"}>
							<Form.Control
								autoComplete="off"
								value={subject ?? ""}
								placeholder=""
								onChange={e => setSubject(e.target.value)}
								onKeyDownCapture={onKeyUp}
							/>
						</Form.FloatingLabel>

						<Form.FloatingLabel label="Message">
							<Form.Control
								as="textarea"
								autoComplete="off"
								value={message ?? ""}
								placeholder=""
								onChange={e => setMessage(e.target.value)}
								onKeyDownCapture={onKeyUp}
							/>
						</Form.FloatingLabel>
					</section>
					{errors?.length !== 0 && (
						<section id="errors">
							{errors.map((err, idx) => <div key={idx}>{err}</div>)}
						</section>
					)}
				</section>
			</Modal.Body>
			<Modal.Footer>
				<Button className="btn btn-primary" onClick={save}>Save</Button>
			</Modal.Footer>
		</Modal>
	)
}